import { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getPreferredLanguage,
  localizePath,
  normalizeLanguage,
  shouldSkipLanguagePrefix,
  swapLanguageInPath,
} from "../utils/languageRouting";

/** Navigate while keeping (or applying) the active language prefix. */
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const { lang } = useParams();
  const activeLang = normalizeLanguage(lang || getPreferredLanguage());

  return useCallback(
    (to, options) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }
      if (typeof to === "string") {
        navigate(localizePath(to, activeLang), options);
        return;
      }
      if (to && typeof to === "object" && typeof to.pathname === "string") {
        navigate(
          {
            ...to,
            pathname: localizePath(to.pathname, activeLang),
          },
          options
        );
        return;
      }
      navigate(to, options);
    },
    [navigate, activeLang]
  );
}

/** Switch language via navbar: update i18n + URL together. */
export function useSwitchLanguage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();

  return useCallback(
    (nextLang) => {
      const code = normalizeLanguage(nextLang);
      i18n.changeLanguage(code);
      try {
        localStorage.setItem("irqgyp_lang", code);
      } catch {
        // ignore
      }

      if (shouldSkipLanguagePrefix(location.pathname)) {
        return;
      }

      const nextPath = swapLanguageInPath(
        location.pathname,
        code,
        location.search,
        location.hash
      );
      if (nextPath !== `${location.pathname}${location.search}${location.hash}`) {
        navigate(nextPath, { replace: true });
      } else if (normalizeLanguage(lang) !== code) {
        navigate(nextPath, { replace: true });
      }
    },
    [i18n, navigate, location.pathname, location.search, location.hash, lang]
  );
}
